import { useState, useCallback, type CSSProperties } from 'react';
import { usePromptTimeTravel } from '../../hooks/usePromptTimeTravel';
import type { TimelineFork, ForkDelta } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface ForkControlsProps {
  sessionId: string;
  forks?: TimelineFork[];
  onViewDiff?: (fork: TimelineFork) => void;
  onResume?: (fork: TimelineFork) => void;
  onDelete?: (fork: TimelineFork) => void;
}

const containerStyle: CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  padding: 8,
};

const cardStyle: CSSProperties = {
  border: '2px solid',
  borderColor: '#dfdfdf #808080 #808080 #dfdfdf',
  backgroundColor: '#ece9d8',
  padding: 8,
  marginBottom: 6,
};

const labelBold: CSSProperties = {
  fontWeight: 'bold',
  marginBottom: 4,
};

const metaStyle: CSSProperties = {
  color: '#404040',
  fontSize: 11,
  marginBottom: 4,
};

const deltaStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--xp-selection)',
  marginBottom: 6,
};

const buttonBase: CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
  padding: '2px 8px',
  cursor: 'pointer',
  border: '2px solid',
  borderColor: '#dfdfdf #808080 #808080 #dfdfdf',
  backgroundColor: '#ece9d8',
  marginRight: 4,
};

const deleteButton: CSSProperties = {
  ...buttonBase,
  color: '#cc0000',
};

const forkHereButton: CSSProperties = {
  ...buttonBase,
  fontWeight: 'bold',
  padding: '3px 12px',
  backgroundColor: '#dfdfdf',
};

const emptyStyle: CSSProperties = {
  color: '#808080',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: 16,
};

function describeDelta(delta: ForkDelta): string {
  switch (delta.kind) {
    case 'system-prompt':
      return 'System prompt changed';
    case 'user-prompt':
      return 'User prompt changed';
    case 'skill-add':
      return `Skill added: ${delta.newValue}`;
    case 'skill-remove':
      return `Skill removed: ${delta.oldValue}`;
    case 'skill-edit':
      return `Skill edited: ${delta.field}`;
    case 'model-change':
      return `Model: ${delta.oldValue} -> ${delta.newValue}`;
    case 'lora-toggle': {
      const skills = delta.metadata?.sourceSkillNames ?? [];
      if (skills.length === 0) return 'Specialization toggled';
      if (skills.length === 1) return `Specialized on: ${skills[0]}`;
      return `Specialized on ${skills.length} skills: ${skills.slice(0, 2).join(', ')}${skills.length > 2 ? '…' : ''}`;
    }
    default:
      return delta.field;
  }
}

export function ForkControls({
  sessionId,
  forks = [],
  onViewDiff,
  onResume,
  onDelete,
}: ForkControlsProps) {
  const { currentStepIndex, forkFromHere } = usePromptTimeTravel(sessionId);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [forkLabelInput, setForkLabelInput] = useState('');
  const [showForkInput, setShowForkInput] = useState(false);

  const handleForkHere = useCallback(() => {
    if (!showForkInput) {
      setShowForkInput(true);
      return;
    }
    const label = forkLabelInput.trim() || `Fork at step ${currentStepIndex + 1}`;
    forkFromHere(label);
    setForkLabelInput('');
    setShowForkInput(false);
  }, [showForkInput, forkLabelInput, currentStepIndex, forkFromHere]);

  const handleDelete = useCallback(
    (fork: TimelineFork) => {
      if (confirmDeleteId === fork.id) {
        onDelete?.(fork);
        setConfirmDeleteId(null);
      } else {
        setConfirmDeleteId(fork.id);
      }
    },
    [confirmDeleteId, onDelete],
  );

  const inputStyle: CSSProperties = {
    fontFamily: "var(--xp-font-body)",
    fontSize: 11,
    padding: '2px 4px',
    border: '2px solid',
    borderColor: '#808080 #dfdfdf #dfdfdf #808080',
    marginRight: 4,
    width: 160,
  };

  return (
    <div style={containerStyle} data-testid={testIds.timeline.forkControls}>
      {forks.length === 0 && (
        <div style={emptyStyle}>No forks yet. Fork from the current step to compare outputs.</div>
      )}

      {forks.map((fork) => (
        <div key={fork.id} style={cardStyle} data-testid={testIds.timeline.forkCard(fork.id)}>
          <div style={labelBold}>{fork.label}</div>
          <div style={metaStyle}>Forked at step {fork.forkStepIndex + 1}</div>
          {fork.deltas.length > 0 && (
            <div style={deltaStyle}>
              {fork.deltas.map((d, i) => (
                <div key={i}>{describeDelta(d)}</div>
              ))}
            </div>
          )}
          <div>
            <button
              style={buttonBase}
              onClick={() => onViewDiff?.(fork)}
              type="button"
              data-testid={testIds.timeline.forkViewDiff(fork.id)}
            >
              View Diff
            </button>
            <button
              style={buttonBase}
              onClick={() => onResume?.(fork)}
              type="button"
              data-testid={testIds.timeline.forkResume(fork.id)}
            >
              Resume
            </button>
            <button
              style={deleteButton}
              onClick={() => handleDelete(fork)}
              type="button"
              data-testid={testIds.timeline.forkDelete(fork.id)}
            >
              {confirmDeleteId === fork.id ? 'Confirm Delete' : 'Delete'}
            </button>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
        {showForkInput && (
          <input
            style={inputStyle}
            placeholder="Fork label..."
            value={forkLabelInput}
            onChange={(e) => setForkLabelInput(e.target.value)}
            data-testid={testIds.timeline.forkLabelInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleForkHere();
              if (e.key === 'Escape') setShowForkInput(false);
            }}
            autoFocus
          />
        )}
        <button style={forkHereButton} onClick={handleForkHere} type="button" data-testid={testIds.timeline.forkHere}>
          {showForkInput ? 'Create Fork' : 'Fork Here'}
        </button>
      </div>
    </div>
  );
}
